import { Injectable, inject } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { RealtimeEvent, RealtimeService } from './realtime.service';
import { CacheStore } from '../offline/cache-store.service';

/**
 * Pont entre Supabase Realtime et la mécanique offline existante (cache IDB).
 *
 * <p>Comportement :</p>
 * <ul>
 *   <li>À chaque événement INSERT/UPDATE/DELETE reçu, on invalide les
 *       entrées de cache du <i>type</i> correspondant (par exemple, un INSERT
 *       sur {@code client} purge toutes les réponses {@code GET /client/...}
 *       du cache). Au prochain GET, le composant recharge depuis le serveur
 *       et obtient la valeur fraîche.</li>
 *   <li>Un Subject {@code refresh$} est exposé pour que les composants
 *       intéressés puissent réagir à chaud (refetch programmé, badge "nouveau"…)
 *       sans avoir à écouter directement Realtime.</li>
 * </ul>
 *
 * <p>Cette stratégie est conservative : on ne pousse pas directement les
 * nouvelles valeurs dans les vues affichées (cela demanderait une carte
 * de patches par composant). On préfère invalider et laisser le composant
 * refetch — c'est nettement plus simple et suffit largement pour Niamey où
 * la latence d'un GET en local est sub-100ms.</p>
 */
@Injectable({ providedIn: 'root' })
export class RealtimeCacheBridge {
  private realtime = inject(RealtimeService);
  private cache = inject(CacheStore);

  /** Tables suivies en temps réel par défaut. */
  private readonly DEFAULT_TABLES: Array<{ table: string; type: string }> = [
    { table: 'client',             type: 'client' },
    { table: 'mesure',             type: 'mesure' },
    { table: 'modele',             type: 'modele' },
    { table: 'commande',           type: 'commande' },
    { table: 'commande_article',   type: 'commande' },
    { table: 'commande_paiement',  type: 'commande' },
    { table: 'facture',            type: 'facture' },
    { table: 'depense',            type: 'depense' },
    { table: 'article_stock',      type: 'inventaire' },
    { table: 'mouvement_stock',    type: 'inventaire' },
    { table: 'appnotification',    type: 'notification' },
  ];

  /** Signal de rafraîchissement par type d'entité — les composants s'y abonnent. */
  private readonly _refresh$ = new Subject<{ type: string; event: RealtimeEvent }>();
  readonly refresh$ = this._refresh$.asObservable();

  private subs: Subscription[] = [];

  /**
   * Démarre le pont. À appeler après login OK.
   * Stoppe et nettoie les subscriptions précédentes avant de redémarrer.
   */
  start(): void {
    this.stop();
    for (const { table, type } of this.DEFAULT_TABLES) {
      const sub = this.realtime.subscribeTable(table).subscribe((event) => {
        void this.cache.invalidateByType(type);
        this._refresh$.next({ type, event });
      });
      this.subs.push(sub);
    }
  }

  /** Arrête le pont et libère toutes les subscriptions WebSocket. */
  stop(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.subs = [];
  }
}
